import mustache from "mustache";
import { razaAnimalGrpcCatalogService } from "../../../../../modulos/catalogos/pesaje/ganaderia/services/RazaAnimalGrpcCatalogService";
import { tipoAnimalGrpcCatalogService } from "../../../../../modulos/catalogos/pesaje/ganaderia/services/TipoAnimalGrpcCatalogService";
import { RazaAnimalItem, TipoAnimalItem } from "../../../../../generated/grpc/catalogos_pesaje_pb";
import { CompraAnimalesRequest } from "../../../../../modulos/pesaje/ganaderia/request/CompraAnimalesRequest";
import { addDays, formatFechaCorta, toISODate } from "../../../../../utils/fechaUtils";
import { _2d, _miles } from "../../../../../utils/numberUtils";
import { IFormBodyRender } from "../../../../definitions/form/IFormBodyRender";
import { IFormField } from "../../../../definitions/form/IFormField";
import { InputDateField } from "../../../../implementations/form/date/InputDateField";
import { InputNumberField } from "../../../../implementations/form/number/InputNumberField";
import { InputSelectField } from "../../../../implementations/form/select/InputSelectField";
import { SummaryItemRender } from "../../../../implementations/summary/SummaryItemRender";
import { LoteSelectorField } from "../../../terceros/lote/form/LoteSelectorField";
import template from "./compraFormTemplate.html";

mustache.parse(template);

/** Formulario de compra de animales (Render/Connect). Expone `validate()` para
 *  que el modal contenedor decida si guardar. */
export class CompraFormRender implements IFormBodyRender {
  private readonly _request: CompraAnimalesRequest;

  // === Sección "Datos de la compra" ===
  private readonly _fecha: InputDateField<CompraAnimalesRequest>;
  private readonly _tipo: InputSelectField<
    CompraAnimalesRequest,
    TipoAnimalItem
  >;
  private readonly _raza: InputSelectField<
    CompraAnimalesRequest,
    RazaAnimalItem
  >;
  private readonly _cantidad: InputNumberField<CompraAnimalesRequest>;
  private readonly _pesoPromedio: InputNumberField<CompraAnimalesRequest>;
  private readonly _precioKg: InputNumberField<CompraAnimalesRequest>;

  // === Sección "Resumen financiero" ===
  private readonly _pesoTotal: SummaryItemRender;
  private readonly _costoTotal: SummaryItemRender;
  private readonly _costoPorAnimal: SummaryItemRender;

  // === Sección "Potrero destino" ===
  private readonly _lote: LoteSelectorField<CompraAnimalesRequest>;

  // === Sección "GDP esperado y peso target" ===
  private readonly _gdp: InputNumberField<CompraAnimalesRequest>;
  private readonly _pesoTarget: InputNumberField<CompraAnimalesRequest>;
  private readonly _ganancia: SummaryItemRender;
  private readonly _diasEstimados: SummaryItemRender;
  private readonly _fechaVenta: SummaryItemRender;

  /** Campos con validación (para `validate`). */
  private readonly _fields: IFormField[];
  /** Summaries de solo lectura (connect/dispose). */
  private readonly _summaries: SummaryItemRender[];

  constructor(request: CompraAnimalesRequest) {
    this._request = request;
    this._fecha = this._createFecha();
    this._tipo = this._createTipo();
    this._raza = this._createRaza();
    this._cantidad = this._createCantidad();
    this._pesoPromedio = this._createPesoPromedio();
    this._precioKg = this._createPrecioKg();

    this._pesoTotal = this._createSummary(
      "Peso total",
      "cantidad × peso promedio",
    );
    this._costoTotal = this._createSummary(
      "Costo total compra",
      "peso total × precio kg",
    );
    this._costoPorAnimal = this._createSummary(
      "Costo por animal",
      "promedio por cabeza",
    );

    this._lote = this._createLote();

    this._gdp = this._createGdp();
    this._pesoTarget = this._createPesoTarget();
    this._ganancia = this._createSummary("Ganancia de peso", "kg/animal");
    this._diasEstimados = this._createSummary(
      "Días estimados en finca",
      "desde fecha de compra",
    );
    this._fechaVenta = this._createSummary("Fecha est. de venta", "estimada");

    this._fields = [
      this._fecha,
      this._tipo,
      this._raza,
      this._cantidad,
      this._pesoPromedio,
      this._precioKg,
      this._lote,
      this._gdp,
      this._pesoTarget,
    ];
    this._summaries = [
      this._pesoTotal,
      this._costoTotal,
      this._costoPorAnimal,
      this._ganancia,
      this._diasEstimados,
      this._fechaVenta,
    ];
  }

  render(): string {
    return mustache.render(template, {
      fecha: this._fecha.render(),
      tipo: this._tipo.render(),
      raza: this._raza.render(),
      cantidad: this._cantidad.render(),
      pesoPromedio: this._pesoPromedio.render(),
      precioKg: this._precioKg.render(),
      pesoTotal: this._pesoTotal.render(),
      costoTotal: this._costoTotal.render(),
      costoPorAnimal: this._costoPorAnimal.render(),
      lote: this._lote.render(),
      gdp: this._gdp.render(),
      pesoTarget: this._pesoTarget.render(),
      ganancia: this._ganancia.render(),
      diasEstimados: this._diasEstimados.render(),
      fechaVenta: this._fechaVenta.render(),
    });
  }

  connect(): void {
    this._fields.forEach((field) => field.connect());
    this._summaries.forEach((summary) => summary.connect());

    // La raza depende del tipo: al cambiar el tipo, resetea y recarga la raza.
    this._tipo.setOnChange(() => {
      this._request.razaAnimalId = "";
      void this._raza.reload();
    });

    // Resumen + proyección en vivo. Peso promedio afecta a ambos, por eso se
    // recalculan juntos ante cualquier campo que participe.
    const onChange = () => {
      this._refreshResumen();
      this._refreshProyeccion();
    };
    this._cantidad.setOnChange(onChange);
    this._pesoPromedio.setOnChange(onChange);
    this._precioKg.setOnChange(onChange);
    this._gdp.setOnChange(onChange);
    this._pesoTarget.setOnChange(onChange);
    this._fecha.setOnChange(onChange);

    this._refreshResumen();
    this._refreshProyeccion();
  }

  dispose(): void {
    this._fields.forEach((field) => field.dispose());
    this._summaries.forEach((summary) => summary.dispose());
  }

  /** Valida todos los campos; retorna `true` si son válidos. */
  validate(): boolean {
    let isValid = true;
    this._fields.forEach((field) => {
      field.showError();
      if (field.getError()) {
        if (isValid) field.focus();
        isValid = false;
      }
    });
    return isValid;
  }

  // ─── Sección "Datos de la compra" — factories de campos ───

  /** Fecha de compra (máximo hoy). */
  private _createFecha(): InputDateField<CompraAnimalesRequest> {
    return new InputDateField(this._request, {
      label: "Fecha de compra",
      description: "Seleccione la fecha de compra",
      max: toISODate(new Date()),
      get: (r) => r.fechaCompra,
      set: (r, v) => {
        r.fechaCompra = v;
      },
    });
  }

  /** Tipo de animal (catálogo). */
  private _createTipo(): InputSelectField<
    CompraAnimalesRequest,
    TipoAnimalItem
  > {
    return new InputSelectField(
      this._request,
      {
        label: "Tipo de animal",
        description: "Seleccione el tipo de animal",
        emptyLabel: "Seleccione tipo",
        service: tipoAnimalGrpcCatalogService,
        get: (r) => r.tipoAnimalId,
        set: (r, v) => {
          r.tipoAnimalId = v ?? "";
        },
        getLabel: (tipo) => tipo.nombre,
      },
      { isRequired: true },
    );
  }

  /** Raza (catálogo, filtrada por el tipo seleccionado). */
  private _createRaza(): InputSelectField<
    CompraAnimalesRequest,
    RazaAnimalItem
  > {
    return new InputSelectField(
      this._request,
      {
        label: "Raza",
        description: "Seleccione la raza del animal",
        emptyLabel: "Seleccione raza",
        service: razaAnimalGrpcCatalogService,
        get: (r) => r.razaAnimalId,
        set: (r, v) => {
          r.razaAnimalId = v ?? "";
        },
        getLabel: (raza) => raza.raza,
        filter: (raza, req) => req.tipoAnimalId === raza.tipoId,
      },
      { isRequired: true },
    );
  }

  /** Cantidad de animales (entero, mínimo 1). */
  private _createCantidad(): InputNumberField<CompraAnimalesRequest> {
    return new InputNumberField(
      this._request,
      {
        label: "Cantidad de animales",
        description: "Número de animales de la compra",
        get: (r) => r.cantidadAnimalesCompra,
        set: (r, v) => {
          r.cantidadAnimalesCompra = v;
        },
      },
      { isRequired: true, min: 1 },
    );
  }

  /** Peso promedio de entrada por animal (kg, entero, mínimo 1). */
  private _createPesoPromedio(): InputNumberField<CompraAnimalesRequest> {
    return new InputNumberField(
      this._request,
      {
        label: "Peso promedio entrada (kg)",
        description: "Peso promedio de cada animal al ingresar",
        get: (r) => r.pesoPromedioEntrada,
        set: (r, v) => {
          r.pesoPromedioEntrada = v;
        },
      },
      { isRequired: true, min: 1 },
    );
  }

  /** Precio por kg (entero, mínimo 1). */
  private _createPrecioKg(): InputNumberField<CompraAnimalesRequest> {
    return new InputNumberField(
      this._request,
      {
        label: "Precio por kg ($)",
        description: "Precio pagado por kilo",
        get: (r) => r.precioKg,
        set: (r, v) => {
          r.precioKg = v;
        },
      },
      { isRequired: true, min: 1 },
    );
  }

  // ─── Sección "Resumen financiero" ───

  /** Recalcula peso total, costo total y costo por animal. */
  private _refreshResumen(): void {
    const cant = this._request.cantidadAnimalesCompra;
    const peso = this._request.pesoPromedioEntrada;
    const precio = this._request.precioKg;
    const pesoTotal = cant * peso;
    const costoTotal = pesoTotal * precio;
    const costoPorAnimal = cant > 0 ? costoTotal / cant : 0;

    this._pesoTotal.setValue(pesoTotal > 0 ? `${_miles(pesoTotal)} kg` : null);
    this._costoTotal.setValue(costoTotal > 0 ? `$ ${_miles(costoTotal)}` : null);
    this._costoPorAnimal.setValue(
      costoPorAnimal > 0 ? `$ ${_miles(costoPorAnimal)}` : null,
    );
  }

  /** Crea un item de resumen (label + descripción). */
  private _createSummary(label: string, description: string): SummaryItemRender {
    return new SummaryItemRender({ label, description });
  }

  // ─── Sección "Potrero destino" ───

  /** Selector de potrero (lote de entrada). */
  private _createLote(): LoteSelectorField<CompraAnimalesRequest> {
    return new LoteSelectorField(
      this._request,
      {
        title: "Potrero destino",
        get: (r) => r.loteEntradaId,
        set: (r, v) => {
          r.loteEntradaId = v;
        },
      },
      { isRequired: true },
    );
  }

  // ─── Sección "GDP esperado y peso target" ───

  /** GDP esperado (gramos/día). */
  private _createGdp(): InputNumberField<CompraAnimalesRequest> {
    return new InputNumberField(
      this._request,
      {
        label: "GDP esperado (g/día)",
        description: "Ganancia diaria de peso esperada en gramos.",
        get: (r) => r.gdpEsperadoGr ?? 0,
        set: (r, v) => {
          r.gdpEsperadoGr = v;
        },
      },
      { isRequired: true },
    );
  }

  /** Peso objetivo de salida por animal (kg, decimal). */
  private _createPesoTarget(): InputNumberField<CompraAnimalesRequest> {
    return new InputNumberField(
      this._request,
      {
        label: "Peso target de salida (kg)",
        description: "Peso objetivo al finalizar la estadía.",
        decimal: true,
        get: (r) => r.pesoTargetSalida,
        set: (r, v) => {
          r.pesoTargetSalida = v;
        },
      },
      { isRequired: true },
    );
  }

  /** Recalcula ganancia de peso, días estimados en finca y fecha estimada de venta. */
  private _refreshProyeccion(): void {
    const ganancia =
      this._request.pesoTargetSalida - this._request.pesoPromedioEntrada;
    this._ganancia.setValue(ganancia > 0 ? `${_2d(ganancia)} kg` : null);

    const gdpKg = (this._request.gdpEsperadoGr ?? 0) / 1000;
    const dias = gdpKg > 0 && ganancia > 0 ? Math.ceil(ganancia / gdpKg) : 0;
    this._diasEstimados.setValue(dias > 0 ? `${dias} días` : null);

    const fechaCompra = this._request.fechaCompra;
    if (dias > 0 && fechaCompra) {
      this._fechaVenta.setValue(formatFechaCorta(addDays(fechaCompra, dias)));
    } else {
      this._fechaVenta.setValue(null);
    }
  }
}
